import { useCallback, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { getClassLevels, getJobPositions, getLeaveTypes } from "../services/referenceServices";
import { LeaveType } from "../types/leaveTypeTypes";
import { JobPosition } from "types/staffTypes";
import { PaymentMethodType } from "types/paymentMethodTypes";
import { getAllPaymentMethods } from "services/paymentMethodService";

export interface ClassLevel {
  id: string;
  name: string;
  code?: string;
  order?: number;
  [k: string]: any;
}

export const useReference = () => {
  const { t } = useTranslation();
  const [classLevels, setClassLevels] = useState<ClassLevel[]>([]);
  const [loadingClassLevels, setLoadingClassLevels] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jobPositions, setJobPositions] = useState<JobPosition[]>([]);
  const [loadingJobPositions, setLoadingJobPositions] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodType[]>([]);

  const fetchClassLevels = async () => {
    setLoadingClassLevels(true);
    try {
      const res = await getClassLevels();
      setClassLevels(res.data);
      setError(null);
    } catch (err) {
      console.error(err);
      const msg = t("failed_to_fetch_class_levels") || "Failed to fetch class levels";
      message.error(msg);
      setError(msg);
    } finally {
      setLoadingClassLevels(false);
    }
  };

  const fetchLeaveTypes = async () => {
    setLoadingLeaveTypes(true);
    try {
      const res = await getLeaveTypes();
      setLeaveTypes(res);
      setError(null);
    } catch (err) {
      console.error(err);
      const msg = t("failed_to_fetch_leave_types") || "Failed to fetch leave types";
      message.error(msg);
      setError(msg);
    } finally {
      setLoadingLeaveTypes(false);
    }
  };

  const fetchJobPositions = async () => {
    setLoadingJobPositions(true);
    try {
      const res = await getJobPositions();
      setJobPositions(res);
      setError(null);
    } catch (err) {
      console.error(err);
      const msg = t("failed_to_fetch_job_positions") || "Failed to fetch job positions";
      message.error(msg);
      setError(msg);
    } finally {
      setLoadingJobPositions(false);
    }
  };


  const fetchPaymentMethods = useCallback(async () =>{
    setLoadingPaymentMethods(true);
    try {
      const response = await getAllPaymentMethods();
      setPaymentMethods(response.data);
    } catch (error) {
      console.error(error);
      const msg = t("failed_to_fetch_payment_methods") || "Failed to fetch payment methods";
      message.error(msg);
      setError(msg);
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, []);




  return {
    classLevels,
    loadingClassLevels,
    fetchClassLevels,
    leaveTypes,
    loadingLeaveTypes,
    fetchLeaveTypes,
    jobPositions,
    loadingJobPositions,
    fetchJobPositions,
    paymentMethods,
    loadingPaymentMethods,
    fetchPaymentMethods,
    error,
  };
};

export default useReference;
